/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — stmt-income.ts
   buildIncomeStatement() and shared helpers (mergeLabels,
   buildNoteTable, buildComparativeSection)
   ═══════════════════════════════════════════════════════════════════════ */

import { state, type LineItem } from './state';
import {
  prepareStatementData,
  restoreStatementData,
} from './consolidation';
import {
  sum,
  rows,
  hasPriorData,
  tr,
  sectionHeader,
  columnHeaders,
  stmtHeader,
  esc,
  cur,
  fmt,
  meta,
  el,
} from './utils';

// ─── Types ─────────────────────────────────────────────────────────────────

interface NoteTableRow {
  label: string;
  current: number;
  prior: number;
  isTotal?: boolean;
}

export interface MergedItem {
  label: string;
  cur: number;
  prior: number | null;
}

// ─── Shared Helpers ────────────────────────────────────────────────────────

// Helper to merge current and prior row labels for multi-period display
export function mergeLabels(
  curRows: LineItem[],
  priorRows: LineItem[],
  multi: boolean
): MergedItem[] {
  if (!multi) {
    return curRows.map((r: LineItem) => ({
      label: r.label,
      cur: parseFloat(String(r.amount)) || 0,
      prior: null,
    }));
  }
  const map = new Map<string, MergedItem>();
  curRows.forEach((r: LineItem) => {
    map.set(r.label, {
      label: r.label,
      cur: parseFloat(String(r.amount)) || 0,
      prior: 0,
    });
  });
  priorRows.forEach((r: LineItem) => {
    if (map.has(r.label)) {
      map.get(r.label)!.prior = parseFloat(String(r.amount)) || 0;
    } else {
      map.set(r.label, {
        label: r.label,
        cur: 0,
        prior: parseFloat(String(r.amount)) || 0,
      });
    }
  });
  return Array.from(map.values());
}

// ─── Comparative Note Table Builder ────────────────────────────────────────

function buildNoteTable(tableRows: NoteTableRow[]): string {
  const m = meta();
  const c: string = cur();
  const multi: boolean = hasPriorData();
  let html = '<table class="note-table">';
  html +=
    '<thead><tr><th></th><th>' + esc(m.period || 'Current') + '</th>';
  if (multi) html += '<th>' + esc(m.priorPeriod || 'Prior') + '</th>';
  html += '</tr></thead><tbody>';
  tableRows.forEach((r: NoteTableRow) => {
    const cls: string = r.isTotal ? 'nt-total' : '';
    html += '<tr class="' + cls + '">';
    html += '<td>' + esc(r.label) + '</td>';
    html += '<td>' + c + fmt(r.current) + '</td>';
    if (multi) html += '<td>' + c + fmt(r.prior) + '</td>';
    html += '</tr>';
  });
  html += '</tbody></table>';
  return html;
}

export function buildComparativeSection(
  sectionKey: string,
  labelFilter?: RegExp
): string {
  const multi: boolean = hasPriorData();
  let curItems: LineItem[] = rows(state.currentData, sectionKey);
  if (labelFilter)
    curItems = curItems.filter((r: LineItem) => labelFilter.test(r.label));
  if (curItems.length === 0) return '';

  const tableRows: NoteTableRow[] = [];
  let curTotal = 0;
  let priorTotal = 0;
  curItems.forEach((r: LineItem) => {
    const amt: number = parseFloat(String(r.amount)) || 0;
    curTotal += amt;
    let priorAmt = 0;
    if (multi) {
      let priorItems: LineItem[] = rows(state.priorData, sectionKey);
      if (labelFilter)
        priorItems = priorItems.filter((p: LineItem) =>
          labelFilter.test(p.label)
        );
      const match: LineItem | undefined = priorItems.find(
        (p: LineItem) => p.label === r.label
      );
      if (match) priorAmt = parseFloat(String(match.amount)) || 0;
    }
    priorTotal += priorAmt;
    tableRows.push({ label: r.label, current: amt, prior: priorAmt });
  });

  if (multi) {
    let priorItems: LineItem[] = rows(state.priorData, sectionKey);
    if (labelFilter)
      priorItems = priorItems.filter((p: LineItem) =>
        labelFilter.test(p.label)
      );
    priorItems.forEach((p: LineItem) => {
      if (!curItems.find((c: LineItem) => c.label === p.label)) {
        const amt: number = parseFloat(String(p.amount)) || 0;
        priorTotal += amt;
        tableRows.push({ label: p.label, current: 0, prior: amt });
      }
    });
  }

  tableRows.push({
    label: 'Total',
    current: curTotal,
    prior: priorTotal,
    isTotal: true,
  });
  return buildNoteTable(tableRows);
}

// ─── Income Statement ───────────────────────────────────────────────────────

/**
 * Builds the income statement HTML table from current/prior period data
 * and renders it to the DOM.
 */
export function buildIncomeStatement(): void {
  prepareStatementData();
  try {
  const multi: boolean = hasPriorData();
  const totalRevenue: number = sum(state.currentData, 'revenue');
  const totalCOGS: number = sum(state.currentData, 'cogs');
  const grossProfit: number = totalRevenue - totalCOGS;
  const totalOpex: number = sum(state.currentData, 'opex');
  const operatingIncome: number = grossProfit - totalOpex;
  const totalOther: number = sum(state.currentData, 'other');
  const netIncome: number = operatingIncome + totalOther;

  let pTotalRevenue = 0;
  let pTotalCOGS = 0;
  let pGrossProfit = 0;
  let pTotalOpex = 0;
  let pOperatingIncome = 0;
  let pTotalOther = 0;
  let pNetIncome = 0;
  if (multi) {
    pTotalRevenue = sum(state.priorData, 'revenue');
    pTotalCOGS = sum(state.priorData, 'cogs');
    pGrossProfit = pTotalRevenue - pTotalCOGS;
    pTotalOpex = sum(state.priorData, 'opex');
    pOperatingIncome = pGrossProfit - pTotalOpex;
    pTotalOther = sum(state.priorData, 'other');
    pNetIncome = pOperatingIncome + pTotalOther;
  }

  let html: string = stmtHeader('Income Statement');
  const tableClass: string = multi ? 'stmt-table multi-period' : 'stmt-table';
  if (multi) {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-current"><col class="col-prior"></colgroup><tbody>';
  } else {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-amount"><col class="col-total"></colgroup><tbody>';
  }
  html += columnHeaders(multi);

  // Revenue
  html += sectionHeader('Revenue', multi);
  const revRows: LineItem[] = rows(state.currentData, 'revenue');
  const pRevRows: LineItem[] = multi ? rows(state.priorData, 'revenue') : [];
  const allRevLabels: MergedItem[] = mergeLabels(revRows, pRevRows, multi);
  allRevLabels.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Revenue', indent: false, currentAmt: totalRevenue, priorAmt: multi ? pTotalRevenue : null, isTotal: false, cls: '', multi });

  // COGS
  html += sectionHeader('Cost of Goods Sold', multi);
  const cogsRows: LineItem[] = rows(state.currentData, 'cogs');
  const pCogsRows: LineItem[] = multi ? rows(state.priorData, 'cogs') : [];
  const allCogsLabels: MergedItem[] = mergeLabels(cogsRows, pCogsRows, multi);
  allCogsLabels.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Cost of Goods Sold', indent: false, currentAmt: totalCOGS, priorAmt: multi ? pTotalCOGS : null, isTotal: false, cls: '', multi });

  html += tr({ label: 'Gross Profit', indent: false, currentAmt: grossProfit, priorAmt: multi ? pGrossProfit : null, isTotal: true, cls: '', multi });

  // OpEx
  html += sectionHeader('Operating Expenses', multi);
  const opexRows: LineItem[] = rows(state.currentData, 'opex');
  const pOpexRows: LineItem[] = multi ? rows(state.priorData, 'opex') : [];
  const allOpexLabels: MergedItem[] = mergeLabels(opexRows, pOpexRows, multi);
  allOpexLabels.forEach((item: MergedItem) => {
    html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
  });
  html += tr({ label: 'Total Operating Expenses', indent: false, currentAmt: totalOpex, priorAmt: multi ? pTotalOpex : null, isTotal: false, cls: '', multi });

  html += tr({ label: 'Operating Income', indent: false, currentAmt: operatingIncome, priorAmt: multi ? pOperatingIncome : null, isTotal: true, cls: '', multi });

  // Other
  if (
    rows(state.currentData, 'other').length ||
    (multi && rows(state.priorData, 'other').length)
  ) {
    html += sectionHeader('Other Income / (Expense)', multi);
    const otherRows: LineItem[] = rows(state.currentData, 'other');
    const pOtherRows: LineItem[] = multi ? rows(state.priorData, 'other') : [];
    const allOtherLabels: MergedItem[] = mergeLabels(otherRows, pOtherRows, multi);
    allOtherLabels.forEach((item: MergedItem) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
    });
    html += tr({ label: 'Total Other Income / (Expense)', indent: false, currentAmt: totalOther, priorAmt: multi ? pTotalOther : null, isTotal: false, cls: '', multi });
  }

  html += tr({ label: 'Net Income', indent: false, currentAmt: netIncome, priorAmt: multi ? pNetIncome : null, isTotal: true, cls: '', multi });

  html += '</tbody></table>';
  const incomeEl = el('income-statement');
  if (incomeEl) incomeEl.innerHTML = html;
  } finally { restoreStatementData(); }
}
